import { useEffect } from 'react';
import { AxiosError } from 'axios';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { validate as uuidValidate } from 'uuid';

import { useActions } from 'shared/hooks';
import { RootState } from 'rootReducer';
import surveyAPI from 'api/base';

import { getContent } from 'store/survey/actions';
import { ROUTES } from 'shared/routes/constants';

import {
  selectIsAuthorized,
  selectIsAuthorizing,
  selectRedirectTo,
  selectBlockType,
  selectContent,
} from './selectors';

import * as topLevelLoadingSpinnerSelectors from 'uicontainers/apps/survey/_shared/TopLevelLoadingSpinner/selectors';

export const useSurveyRouteConfigActions = () =>
  useActions({
    getContent,
  });

export const useSurveyRouteConfigSelectors = () =>
  useSelector((state: RootState) => {
    return {
      isAuthorized: selectIsAuthorized(state),
      isAuthorizing: selectIsAuthorizing(state),
      redirectTo: selectRedirectTo(state),
      blockType: selectBlockType(state),
      content: selectContent(state),
      shouldShowTopLevelLoadingSpinner: topLevelLoadingSpinnerSelectors.selectShouldShowTopLevelLoadingSpinner(
        state,
      ),
    };
  });

export const useAuthorize = () => {
  const { getContent } = useSurveyRouteConfigActions();
  const { redirectTo } = useSurveyRouteConfigSelectors();
  const url = window.location.href;
  const history = useHistory();
  const appendTemplateParam = (path: string) => {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('survey_template', 'SURVEY_2025');
    return url.pathname + url.search;
  };

  // Necessary to trigger re-render if location changes
  useLocation();

  const fetchContentAndRedirectIfNeeded = async (url: string) => {
    const parts = url.split('/');
    const uuidValidParts = parts.map((part) => uuidValidate(part)).filter((isValid) => isValid);

    // Routes that don't require UUIDs (like panel)
    const routesWithoutUUID = [ROUTES.SURVEY.PATHS.PANEL];

    const currentPath = window.location.pathname;
    const isRouteWithoutUUID = routesWithoutUUID.some((route) => {
      // Convert route with parameters to regex pattern
      const routePattern = route.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${routePattern}$`);
      return regex.test(currentPath);
    });

    const referralMatch = currentPath.match(/\/referrals\/([ev])\/([a-f0-9-]+)/i);
    if (referralMatch) {
      const [, , uuid] = referralMatch;
      try {
        const response = await surveyAPI.get(`/referrals/e/${uuid}/`);
        const data = response.data || response;
        if (data.redirect && data.redirect_url) {
          window.location.href = data.redirect_url;
          return;
        }
      } catch (error) {
        console.error('Error checking referral redirect:', error);
      }
    }

    // If it's a route that doesn't require UUID, allow to continue
    if (uuidValidParts.length < 1 && !isRouteWithoutUUID) {
      history.push(appendTemplateParam(ROUTES.SURVEY.PATHS.ERROR));
      return;
    }

    try {
      await getContent({ url });
    } catch (e) {
      const error = e as AxiosError;
      if (error && error.response?.status === 500) {
        history.push(appendTemplateParam(ROUTES.SURVEY.PATHS.ERROR));
      }
    }
  };

  useEffect(() => {
    fetchContentAndRedirectIfNeeded(url);
  }, [url]);

  useEffect(() => {
    if (redirectTo) {
      let newRedirectTo = redirectTo;
      if (process.env.REACT_APP_ENV === 'local') {
        newRedirectTo = redirectTo.replace('localhost', 'localhost:3000');
      }
      const isCustom =
        (url.includes('/a/') && !newRedirectTo.includes('/o/')) ||
        (url.includes('/i/') && newRedirectTo.includes('/a/'));
      if (newRedirectTo !== url && !isCustom) {
        window.location.assign(newRedirectTo);
      } else if (newRedirectTo === url) {
        history.push(appendTemplateParam(ROUTES.SURVEY.PATHS.ERROR));
      }
    }
  }, [redirectTo, url]);
};
